import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { TimeoutError, trackError } from '#api/errors.ts';
import { makeHostQueryOptions } from '#api/hosts.queries.ts';
import { sendCommand } from '#api/hosts.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { SelectHost } from '#components/patterns/entity-select/host/SelectHost.tsx';
import type { Host } from '#dn-types/host.ts';
import { getIpV4, getIpV6 } from '#util/hostIps.ts';
import { DebugModal } from './DebugModal.tsx';
import styles from './CreateTunnelDialog.module.css';

type Props = {
  onClose: () => void;
  onSuccess: (data: Record<string, unknown>) => void;
  host: Host;
  debugElement: HTMLElement | ((element: HTMLElement) => HTMLElement | null) | null;
};

export function CreateTunnelDialog({ onSuccess, onClose, host, debugElement }: Props) {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      host: '',
    },
  });
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const targetHostId = data.host;
      const queryOptions = makeHostQueryOptions(targetHostId);
      const { data: targetHost } = await queryClient.ensureQueryData(queryOptions);
      const target = getIpV4(targetHost) ?? getIpV6(targetHost);
      if (!target) {
        setError('root.global', { message: 'Selected host has no IP address' });
        return;
      }
      const result = await sendCommand(host.id, 'CreateTunnel', { target });
      onSuccess(result);
    } catch (e) {
      if (e instanceof TimeoutError) {
        setError('root.global', { message: 'Unable to contact dnclient' });
      } else {
        trackError(e);
        setError('root.global', { message: 'Something went wrong, please try again' });
      }
    }
  });

  return (
    <DebugModal onClose={onClose} isOpen={true} portalElement={debugElement}>
      <form action="POST" onSubmit={onSubmit}>
        <div className={styles.CreateTunnelDialog_form}>
          <SelectHost
            name="host"
            label="Select host to create tunnel"
            control={control}
            rules={{ required: 'Please choose a host' }}
            hasError={Boolean(errors.host?.message)}
            className="grow mw-0"
            menuProps={{ overlap: true }}
            filterHosts={({ id, networkID }) => id !== host.id && networkID === host.networkID}
          />
          <Button className="pad-16" variant="secondary" type="submit" disabled={isSubmitting}>
            Go
          </Button>
        </div>
        {errors.host?.message && <ErrorMessage>{errors.host.message}</ErrorMessage>}
        {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
      </form>
    </DebugModal>
  );
}
